import { c as _c } from "react/compiler-runtime";
import { PhoneInput } from '@repo/features/phone';
import { usePosthogCapture } from '@repo/sdk/analytics/posthog';
import { useSession } from '@repo/sdk/auth';
import { useForm, zodResolver } from '@repo/sdk/form';
import { useMutation } from '@repo/sdk/tanstack';
import { z } from '@repo/sdk/zod';
import { Button, Form, FormControl, FormField, FormItem, FormMessage, Input, Spinner, toast } from '@repo/ui/components';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { registerAction } from '@/app/_components/welcome-modal/action.ts';
import { useWelcomeModalContext } from '@/app/_components/welcome-modal/context.tsx';
const NewUserForm = () => {
  const $ = _c(41);
  const {
    email,
    setIsOpen
  } = useWelcomeModalContext();
  const session = useSession();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const schema = z.object({
      name: z.string().min(2, {
        message: "Name is required"
      }),
      phone: z.string().optional()
    });
    t0 = useForm;
    t1 = {
      defaultValues: {
        name: "",
        phone: ""
      },
      resolver: zodResolver(schema)
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  const form = t0(t1);
  const {
    posthogCapture
  } = usePosthogCapture();
  let t2;
  if ($[2] !== posthogCapture) {
    t2 = () => posthogCapture("welcome: new user enters registration view");
    $[2] = posthogCapture;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  const pathname = usePathname();
  const params = useSearchParams();
  let t4;
  if ($[5] !== email || $[6] !== params || $[7] !== pathname || $[8] !== posthogCapture || $[9] !== session) {
    t4 = async data => {
      posthogCapture("welcome: new user registration attempt");
      if (!data.phone) {
        posthogCapture("welcome: new user skips phone number");
        await registerAction({
          email,
          name: data.name,
          redirectTo: `${pathname}?${params}`
        });
      } else {
        posthogCapture("welcome: new user provides phone number");
        await registerAction({
          ...data,
          email,
          redirectTo: `${pathname}?${params}`
        });
      }
      const updatedSession = await session.update();
      return !!updatedSession?.user?.id;
    };
    $[5] = email;
    $[6] = params;
    $[7] = pathname;
    $[8] = posthogCapture;
    $[9] = session;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== setIsOpen) {
    t5 = data_0 => {
      if (data_0) {
        setIsOpen(false);
      }
    };
    $[11] = setIsOpen;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = {
      mutationFn: t4,
      onError: _temp,
      onSuccess: t5
    };
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const {
    isPending,
    mutate: register
  } = useMutation(t6);
  let t7;
  if ($[16] !== form || $[17] !== register) {
    let t8;
    if ($[19] !== register) {
      t8 = data_1 => register(data_1);
      $[19] = register;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    t7 = form.handleSubmit(t8);
    $[16] = form;
    $[17] = register;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  let t9;
  if ($[21] !== form.control) {
    t8 = <FormField control={form.control} name="phone" render={_temp2} />;
    t9 = <FormField control={form.control} name="name" render={_temp3} />;
    $[21] = form.control;
    $[22] = t8;
    $[23] = t9;
  } else {
    t8 = $[22];
    t9 = $[23];
  }
  let t10;
  if ($[24] !== posthogCapture) {
    t10 = () => posthogCapture("welcome: new user registration button click");
    $[24] = posthogCapture;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== isPending) {
    t11 = isPending && <Spinner />;
    $[26] = isPending;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== isPending || $[29] !== t10 || $[30] !== t11) {
    t12 = <Button className="font-semibold" disabled={isPending} onClick={t10} type="submit">GET QUOTE NOW {t11}</Button>;
    $[28] = isPending;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="text-center text-sm text-balance text-gray-600">* Terms and Conditions apply.</p>;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12 || $[34] !== t7 || $[35] !== t8 || $[36] !== t9) {
    t14 = <form className="flex flex-col gap-4" onSubmit={t7}>{t8}{t9}{t12}{t13}</form>;
    $[33] = t12;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== form || $[39] !== t14) {
    t15 = <Form {...form}>{t14}</Form>;
    $[38] = form;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
};
export default NewUserForm;
function _temp(error) {
  console.error(error);
  toast.error(error.message ?? "Something went wrong, please get in touch with us");
}
function _temp2(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormControl><PhoneInput {...field} /></FormControl><FormMessage /></FormItem>;
}
function _temp3(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormControl><Input {...field_0} placeholder="Name" /></FormControl><FormMessage /></FormItem>;
}